'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DepartmentShowcaseData } from '@/lib/home-v2/data';
import { Btn, fmt, formatCount, type Labels } from './ui';
import { ProductTile } from './product-tile';

const TONES = {
  light: { bg: 'bg-white', text: 'text-hv-ink', sub: 'text-hv-soft', num: 'text-hv-orange', chip: 'border-hv-line', btn: 'dark' as const, arrow: 'border-hv-line bg-white text-hv-ink hover:border-hv-ink' },
  dark: { bg: 'bg-hv-ink', text: 'text-white', sub: 'text-white/70', num: 'text-hv-yellow', chip: 'border-[#4A4846]', btn: 'white' as const, arrow: 'border-[#4A4846] bg-hv-raised text-white hover:border-white' },
  tint: { bg: 'bg-hv-card', text: 'text-hv-ink', sub: 'text-hv-soft', num: 'text-hv-orange', chip: 'border-[#D6CFC3]', btn: 'dark' as const, arrow: 'border-[#D6CFC3] bg-white text-hv-ink hover:border-hv-ink' },
};

/** Tracks whether a horizontal scroller can move left/right, so arrows only show when useful. */
function useScroller(dep: unknown) {
  const ref = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: 0 });
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update, dep]);
  const scrollBy = (dir: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  return { ref, edges, scrollBy };
}

function Arrow({ dir, disabled, onClick, cls }: { dir: 1 | -1; disabled: boolean; onClick: () => void; cls: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={dir === 1 ? 'Scroll right' : 'Scroll left'}
      className={`flex h-11 w-11 items-center justify-center rounded-xl border transition disabled:cursor-default disabled:opacity-30 ${cls}`}>
      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={dir === 1 ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
      </svg>
    </button>
  );
}

/** One department: tabs per menu group, and a row of uniform range tiles that scrolls with arrows. */
export function DepartmentShowcase({ data, num, labels }: { data: DepartmentShowcaseData; num: string; labels: Labels }) {
  const shopLabel = fmt(data.buttonLabel ?? labels.shopDepartment, { name: data.name.toLowerCase() });
  const [active, setActive] = useState(0);
  const t = TONES[data.tone];
  const group = data.groups[active] ?? data.groups[0];
  const { ref, edges, scrollBy } = useScroller(group.handle);
  const scrollable = edges.left || edges.right;

  return (
    <section className={`${t.bg} ${t.text} mt-4 md:mt-6`} aria-labelledby={`dept-${data.handle}`}>
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 py-7 pl-4 md:gap-6 md:px-16 md:py-12">
        <div className={`grid gap-2 pr-4 md:items-end md:gap-6 md:pr-0 ${num ? 'md:grid-cols-[90px_1fr_auto]' : 'md:grid-cols-[1fr_auto]'}`}>
          {num && <div className={`font-hv-mono text-[11px] md:pb-2 md:text-[13px] ${t.num}`}>{num}</div>}
          <div>
            <h2 id={`dept-${data.handle}`} className="text-[30px] leading-[1.05] tracking-[-0.02em] md:text-[44px]">
              {data.name}{' '}
              {labels.showDesignCounts && <span className="font-hv-mono text-[11px] font-normal uppercase opacity-60 md:text-base">{fmt(labels.designs, { count: formatCount(data.count) })}</span>}
            </h2>
            {data.description && <p className={`mt-2 hidden md:block ${t.sub}`}>{data.description}</p>}
          </div>
          <div className="hidden md:block"><Btn href={`/${data.handle}`} kind={t.btn} arrow>{shopLabel}</Btn></div>
        </div>

        <div className="flex items-center justify-between gap-4 pr-4 md:pl-[var(--hv-indent)] md:pr-0">
          {data.groups.length > 1 ? (
            <div role="tablist" aria-label={`${data.name} filters`} className="scrollbar-hide flex gap-2 overflow-x-auto">
              {data.groups.map((g, k) => (
                <button key={g.handle} role="tab" aria-selected={k === active} onClick={() => setActive(k)}
                  className={`whitespace-nowrap rounded-[10px] px-4 py-2 text-sm transition ${k === active ? 'bg-hv-orange font-semibold text-white' : `border ${t.chip} hover:border-hv-orange`}`}>
                  {data.tabLabels[g.handle] ?? g.name.replace(/^Shop by /i, 'By ').replace(/^By (\w)/, (_, c) => `By ${c.toLowerCase()}`)}
                </button>
              ))}
            </div>
          ) : <div />}
          {scrollable && (
            <div className="hidden flex-shrink-0 gap-2 md:flex">
              <Arrow dir={-1} disabled={!edges.left} onClick={() => scrollBy(-1)} cls={t.arrow} />
              <Arrow dir={1} disabled={!edges.right} onClick={() => scrollBy(1)} cls={t.arrow} />
            </div>
          )}
        </div>

        <div role="tabpanel" className="md:pl-[var(--hv-indent)]">
          <ul ref={ref} className="scrollbar-hide flex snap-x gap-3 overflow-x-auto pb-1 pr-4 md:gap-4 md:pr-0">
            {group.ranges.map((r) => (
              <li key={`${group.handle}-${r.handle}`} className="w-[140px] flex-shrink-0 snap-start md:w-[184px]">
                <Link href={`/${r.handle}`} className="group flex flex-col gap-2.5">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-hv-card">
                    {r.image && <Image src={r.image} alt={r.name} fill sizes="(min-width: 768px) 184px, 140px" className="object-cover transition duration-500 group-hover:scale-105" />}
                  </div>
                  <div className="line-clamp-2 min-h-[2.5em] text-[13px] font-semibold leading-tight md:text-[15px]">{r.name}</div>
                  {labels.showDesignCounts && <div className="-mt-1.5 font-hv-mono text-[10px] uppercase opacity-60 md:text-xs">{fmt(labels.designs, { count: formatCount(r.count) })}</div>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {data.products.length > 0 && (
          <div className="flex flex-col md:pl-[var(--hv-indent)]">
            <h3 className="pr-4 text-lg font-semibold md:pr-0 md:text-xl">{fmt(data.productsTitle, { name: data.name.toLowerCase() })}</h3>
            <ul className={`scrollbar-hide mt-4 flex gap-3 overflow-x-auto border-t ${t.chip} pr-4 pt-6 md:mt-5 md:grid md:grid-cols-4 md:gap-5 md:overflow-visible md:pr-0 md:pt-7`}>
              {data.products.map((p) => (
                <li key={p.id} className="w-[168px] flex-shrink-0 md:w-auto">
                  <ProductTile product={p} badge={labels.newBadge} labels={labels} />
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="pr-4 md:hidden"><Btn href={`/${data.handle}`} kind={t.btn} arrow>{shopLabel}</Btn></div>
      </div>
    </section>
  );
}
